import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import {
  usePublicDonorsList,
  useDeletePublicDonor,
} from "../../../hooks/useAdminPublicDonors";
import { useAdminRole } from "../../../hooks/useAdminRole";
import DataTable from "../../../components/admin/DataTable";
import Pagination from "../../../components/admin/Pagination";
import SearchFilter from "../../../components/admin/SearchFilter";
import ConfirmDialog from "../../../components/admin/ConfirmDialog";
import PublicDonorEditModal from "./PublicDonorEditModal";

const BLOOD_GROUPS = ["A POSITIVE(+)", "A NEGATIVE(-)", "B POSITIVE(+)", "B NEGATIVE(-)", "O POSITIVE(+)", "O NEGATIVE(-)", "AB POSITIVE(+)", "AB NEGATIVE(-)"];
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const AdminPublicDonors = () => {
  const { isSuperAdmin } = useAdminRole();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ bloodGroup: "", district: "", status: "" });
  const [editDonor, setEditDonor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const queryParams = {
    page,
    limit: 20,
    ...(search && { search }),
    ...(filters.bloodGroup && { bloodGroup: filters.bloodGroup }),
    ...(filters.district && { district: filters.district }),
    ...(filters.status && { status: filters.status }),
  };

  const { data, isLoading, error } = usePublicDonorsList(queryParams);
  const deleteMutation = useDeletePublicDonor();

  const handleSearchChange = useCallback((val) => {
    setSearch(val);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((name, val) => {
    setFilters((prev) => ({ ...prev, [name]: val }));
    setPage(1);
  }, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget._id);
      toast.success(`${deleteTarget.name} deleted successfully`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.message || "Failed to delete donor");
    }
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "group", label: "Blood Group" },
    { key: "number", label: "Phone" },
    { key: "district", label: "District" },
    { key: "lastDonation", label: "Last Donation" },
    { key: "status", label: "Status" },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <div className="flex gap-1">
          <button
            className="btn btn-xs btn-info text-white"
            onClick={() => setEditDonor(row)}
          >
            Edit
          </button>
          {isSuperAdmin && (
            <button
              className="btn btn-xs btn-error text-white"
              onClick={() => setDeleteTarget(row)}
            >
              Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="alert alert-error">
        <span>Failed to load donors: {error.message}</span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Public Donors</h1>

      <SearchFilter
        searchValue={search}
        onSearchChange={handleSearchChange}
        filters={[
          {
            name: "bloodGroup",
            label: "All Blood Groups",
            value: filters.bloodGroup,
            options: BLOOD_GROUPS.map((g) => ({ value: g, label: g })),
          },
          {
            name: "status",
            label: "All Status",
            value: filters.status,
            options: STATUS_OPTIONS,
          },
        ]}
        onFilterChange={handleFilterChange}
      />

      <DataTable columns={columns} data={data?.data} loading={isLoading} emptyMessage="No public donors found" />

      {data?.pagination && (
        <div className="flex justify-between items-center mt-4">
          <span className="text-sm text-gray-700">
            Showing {((data.pagination.page - 1) * data.pagination.limit) + 1}–
            {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{" "}
            {data.pagination.total}
          </span>
          <Pagination
            currentPage={data.pagination.page}
            totalPages={data.pagination.totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {editDonor && (
        <PublicDonorEditModal
          donor={editDonor}
          onClose={() => setEditDonor(null)}
        />
      )}

      {isSuperAdmin && (
        <ConfirmDialog
          open={!!deleteTarget}
          title="Delete Public Donor"
          message={`Are you sure you want to delete ${deleteTarget?.name} (${deleteTarget?.group}) — ${deleteTarget?.number}?`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default AdminPublicDonors;
